import { useCallback, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { Heading, Flex, useDisclosure } from '@chakra-ui/react';
import { AgGridColumn } from 'ag-grid-react';
import styled from '@emotion/styled/macro';
import { CancelToken } from 'axios';

import useAutoformMetadata from './hooks/useAutoformMetadata';
import useAutoformTableMetadata from './hooks/useAutoformTableMetadata';
import useAutoformLoadTableDropdowns from './hooks/useAutoformLoadTableDropdowns';
import DataGridWrapper from '../grid/DataGridWrapper';
import DataGrid from '../grid/DataGrid';
import {
    getDataValueObjectFromDto,
    getDtoFromDataValueObject,
    parseColumnSettings,
    parseDateFormat,
    parseNumberFormat,
} from './utils/autoformUtils';
import {
    getAutoformTableData,
    getValidRows,
    saveAutoformRecords,
    formatRow,
    addMultipleAutoformTableRow,
    deleteAutoformTableRows,
    deleteAutoformGridTable,
} from '../../services/autoforms';
import { setSelectedItemForTable } from '../../store/autoform/autoformSlice';
import useCommonToast from '../../hooks/useCommonToast';
import AddRowsButton from '../itemData/AddRowsButton';
import DeleteAllButton from '../../components/itemData/DeleteAllButton';
import { FilterOperationsEnum, INPUT_TYPES, DATA_TYPES } from './const';
import SecondaryIconButton from '../utils/SecondaryIconButton';
import { ReactComponent as FilterIcon } from '../../icons/filter.svg';
import ItemFilters from '../utils/ItemFilters';
import SideDrawer from '../utils/SideDrawer';

const COLUMN_TYPES = {
    [INPUT_TYPES.DropDownForm]: undefined, //'TODO',
    [INPUT_TYPES.TextBox]: undefined,
    [INPUT_TYPES.ComboBox]: 'select',
    [INPUT_TYPES.MonthCalendar]: 'date',
    [INPUT_TYPES.CheckBox]: 'checkbox',
    [INPUT_TYPES.NumericUpDown]: 'number',
    [INPUT_TYPES.ProgressBar]: 'progressBar',
};

const AutoformTableContainer = ({ schemaCode, tableId, hasChildren = false, onSelectItem, children, searchString }) => {
    const intl = useIntl();
    const filterDrawer = useDisclosure();
    const addRowsModal = useDisclosure();
    const [isAddRowsLoading, setIsAddRowsLoading] = useState(false);
    const dispatch = useDispatch();
    const source = useRef(CancelToken.source());
    useAutoformLoadTableDropdowns(tableId);

    const toast = useCommonToast();
    const { metadata, sortedColumns, primaryKeyColumnName, dropdownOptions, selectedItem, descriptionColumnName } =
        useAutoformTableMetadata(tableId);
    const gridApiRef = useRef(null);
    const { metadata: autoformMetadata } = useAutoformMetadata();
    const { selectedItem: selectedParentRecordId } = useAutoformTableMetadata(metadata && metadata.parentId);
    const selectItem = (item) => {
        dispatch(setSelectedItemForTable({ itemId: item[primaryKeyColumnName], tableId }));
        onSelectItem && onSelectItem({ id: item[primaryKeyColumnName], description: item[descriptionColumnName] });
    };

    const formatDropdownOptions = (options) => options?.map((opt) => ({ id: opt.value, description: opt.text }));
    const columnDefinitions = useMemo(() => {
        return sortedColumns
            .filter((col) => col.isVisible)
            .map((col) => {
                if (!(col.inputTypeDescription in COLUMN_TYPES))
                    throw Error(`Unsupported column type ${col.inputTypeDescription}`);

                const settings = parseColumnSettings(col.settings);
                let type = COLUMN_TYPES[col.inputTypeDescription];
                let cellEditorParams = {};
                if (col.inputTypeDescription === INPUT_TYPES.ComboBox && dropdownOptions[col.id]) {
                    cellEditorParams = {
                        cellEditorParams: { options: formatDropdownOptions(dropdownOptions[col.id]) },
                    };
                }
                if (
                    col.inputTypeDescription === INPUT_TYPES.NumericUpDown ||
                    (col.inputTypeDescription === INPUT_TYPES.TextBox &&
                        (col.dataTypeDescription === DATA_TYPES.Int || col.dataTypeDescription === DATA_TYPES.Double))
                ) {
                    cellEditorParams = {
                        cellEditorParams: {
                            min: settings?.NUDMIN,
                            max: settings?.NUDMAX,
                            format: parseNumberFormat(col),
                        },
                    };
                    type = COLUMN_TYPES[INPUT_TYPES.NumericUpDown];
                }
                if (col.inputTypeDescription === INPUT_TYPES.MonthCalendar) {
                    cellEditorParams = {
                        cellEditorParams: {
                            dateFormat: parseDateFormat(col),
                        },
                    };
                }

                return {
                    type,
                    ...cellEditorParams,
                    field: col.name,
                    headerName: col.displayName,
                    headerTooltip: settings?.CELLTOOLTIP,
                    sortable: true,
                    editable: !col.isReadOnly && metadata.canUpdate,
                };
            });
    }, [sortedColumns, metadata, dropdownOptions]);

    const addRowsModalColumns = useMemo(
        () => columnDefinitions.map(({ headerName, field }) => ({ headerName, field })),
        [columnDefinitions]
    );

    const getServerSideData = useCallback(
        async (params) => {
            source.current.cancel();
            source.current = CancelToken.source();
            const parameters = autoformMetadata.parameters.map((parameter) => parameter.value);
            const page = params.request.startRow / 500 + 1;
            const sortData = params.request.sortModel
                .map((sortModel) => ({
                    sortColumnId: sortedColumns.find((def) => def.name === sortModel.colId)?.id,
                    sortByDescending: sortModel.sort !== 'asc',
                }))
                .filter((sort) => !!sort.sortColumnId);
            const filters = [];
            if (searchString) {
                const searchColumn = sortedColumns.find((col) => col.dataTypeDescription === 'System.String');
                if (searchColumn) {
                    filters.push({
                        columnId: searchColumn.id,
                        operation: FilterOperationsEnum.Includes,
                        filterValue: searchString,
                    });
                }
            }
            const tableData = await getAutoformTableData({
                parameters,
                schemaCode,
                page,
                pageSize: 500,
                tableId,
                selectedParentRecordId,
                sortData,
                filters,
                cancelToken: source.current.token,
            });

            if (tableData.totalCount > 0) {
                params.api.hideOverlay();
            } else {
                params.api.showNoRowsOverlay();
            }

            const parsedTableData = tableData.items.map(getDataValueObjectFromDto);

            params.success({ rowData: parsedTableData, rowCount: tableData.totalCount });
        },
        [autoformMetadata, sortedColumns, searchString, schemaCode, tableId, selectedParentRecordId]
    );

    const updateServerSideData = async (rowIds) => {
        const gridApi = gridApiRef.current;
        const rows = rowIds.map((id) => gridApi.getRowNode(id));
        const validRows = getValidRows(rows, metadata.columns);
        const dtoRows = validRows.map((row) => getDtoFromDataValueObject(row, metadata));
        const parameters = autoformMetadata.parameters.map((parameter) => parameter.value);

        if (validRows.length === 0) return;

        try {
            // TODO Merge these requests into one
            await saveAutoformRecords({
                records: dtoRows,
                tableId,
                parameters,
            });
            toast(intl.formatMessage({ id: 'renewable_save_changes_success' }));
        } catch (error) {
            if (error.response?.status !== 401) {
                toast({
                    status: 'error',
                    message: intl.formatMessage({ id: 'common_generic_saving_error' }),
                });
            }
        }
    };

    const onDeleteRow = async (rowIds) => {
        if (metadata.canDelete) {
            const gridApi = gridApiRef.current;
            const selectedRowsIds = rowIds.map((id) => gridApi.getRowNode(id).data[primaryKeyColumnName]);

            if (selectedRowsIds.length === 0) return;

            try {
                // TODO Merge these requests into one
                await deleteAutoformTableRows({
                    recordsIds: selectedRowsIds,
                    tableId,
                });
                toast(intl.formatMessage({ id: 'common_delete_grid_msg' }, { n: selectedRowsIds.length }));

                refetchData();
            } catch (error) {
                if (error.response && error.response.status !== 401) {
                    toast({
                        status: 'error',
                        message: intl.formatMessage({ id: 'common_generic_delete_error' }),
                    });
                }
            }
        }
    };

    const onAddRows = async (rowData) => {
        if (rowData.length === 0) return;
        const records = rowData;
        const columnNames = Object.keys(rowData[0]);
        const columns = metadata.columns.reduce((obj, item) => ({ ...obj, [item.name]: item }), {});
        for (const colName of columnNames) {
            const column = columns[colName];
            if (column.inputTypeDescription === 'ComboBox') {
                let dropdownOptionsMap = dropdownOptions[column.id].reduce((obj, item) => {
                    if (item.text in obj && obj[item.text] < item.value) {
                        return obj;
                    }
                    return { ...obj, [item.text]: item.value };
                }, {});
                for (const index in records) {
                    const dropdownValue = dropdownOptionsMap[records[index][colName]];
                    if (dropdownValue) {
                        records[index][colName] = dropdownValue;
                    }
                }
            }
        }
        const parameters = autoformMetadata.parameters.map((parameter) => parameter.value);

        const successMessage = intl.formatMessage({ id: 'common_generic_item_change_success' });
        const errorMessage = intl.formatMessage({ id: 'common_generic_saving_error' });
        setIsAddRowsLoading(true);
        try {
            // TODO Merge these requests into one
            await addMultipleAutoformTableRow({
                tableId,
                records,
                parentRecordId: selectedParentRecordId,
                parameters,
            });

            toast(successMessage);
            refetchData();

            setIsAddRowsLoading(false);
            addRowsModal.onClose();
        } catch (error) {
            if (error.response && error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: errorMessage,
                });
            }
            setIsAddRowsLoading(false);
        }
    };

    const selectRowForEditing = ({ api, node }) => {
        if (node?.data) {
            selectItem(node.data);
        }
    };

    const refetchData = () => {
        const gridApi = gridApiRef.current;
        gridApi.refreshServerSideStore({
            purge: true,
        });
    };

    const formatParsedNewRows = (parsedData) => parsedData.map((row) => formatRow(row, metadata.columns));

    const onApplyFilters = () => {};

    return (
        <>
            <Heading as="h3" variant="h3" textTransform="capitalize">
                {metadata.name}
            </Heading>

            <Flex justifyContent="flex-end" mb={6}>
                <SecondaryIconButton
                    leftIcon={<CustomFilterIcon />}
                    mr={4}
                    type="button"
                    variant="secondary" //TODO add filter indicator icon
                    onClick={filterDrawer.onOpen}
                    size="sm"
                >
                    <FormattedMessage id="common_filters_btn" />
                </SecondaryIconButton>
                {metadata.canDelete && (
                    <DeleteAllButton
                        isInTabularTab={true}
                        refetchData={refetchData}
                        deleteCallback={() =>
                            deleteAutoformGridTable({ tableId, parentRecordId: selectedParentRecordId })
                        }
                    />
                )}
                {metadata.canCreate && (
                    <AddRowsButton
                        modal={addRowsModal}
                        isLoading={isAddRowsLoading}
                        validate={(V) => V} // TODO Validations
                        format={formatParsedNewRows}
                        columns={addRowsModalColumns}
                        columnSchema={columnDefinitions}
                        addRows={onAddRows}
                    />
                )}
            </Flex>

            <DataGridWrapper>
                <DataGrid
                    serverSideDatasource={{
                        getRows: getServerSideData,
                    }}
                    onGridReady={(params) => (gridApiRef.current = params.api)}
                    onDataChange={updateServerSideData}
                    onDelete={onDeleteRow}
                    rowModelType="serverSide"
                    serverSideStoreType="partial"
                    paginationPageSize={500}
                    cacheBlockSize={500}
                    isInitialRequestSent={true}
                    suppressRowClickSelection={true}
                    noDataMessageId="common_empty_forecast_tabular_area"
                    onCellKeyPress={(params) => {
                        if (params.event.key === 'Enter' && params.column.colId === 'editChild') {
                            selectRowForEditing(params);
                        }
                    }}
                    enableBrowserTooltips={true}
                    tooltipMouseTrack={false}
                >
                    {columnDefinitions.map((def) => (
                        <AgGridColumn key={def.field} {...def} />
                    ))}

                    {hasChildren && (
                        <AgGridColumn
                            type="button"
                            colId="editChild"
                            field="instrumentId"
                            headerName={intl.formatMessage({ id: 'autoform_edit_column_header' })}
                            resizable
                            onCellClicked={(params) => {
                                selectRowForEditing(params);
                            }}
                        />
                    )}
                </DataGrid>
            </DataGridWrapper>
            {children && typeof children === 'function' && children({ selectedItem })}
            {filterDrawer.isOpen && (
                <SideDrawer
                    isOpen
                    header={<FormattedMessage id="common_filter_heading" />}
                    content={<ItemFilters onApplyFilters={onApplyFilters} />} // TODO: apply custom filter forms
                    onClose={filterDrawer.onClose}
                    placement="right"
                />
            )}
        </>
    );
};

const CustomFilterIcon = styled(FilterIcon)`
    path {
        fill: #677380;
    }
`;

export default AutoformTableContainer;
